import React from "react";
import { observer } from "mobx-react-lite";

import pl from "shared/messages/pl";
import { BasicTable, RegularText, TextTableHeader } from "shared/components";
import { constants } from "shared/constants";
import styles from "../commissionsTable.module.scss";
import { Commission, CommissionGroup, CommissionLine } from "view360Card/models";
import { formatPercentageNumber } from "utils/formattingUtils";
import { MobileViewCard, MobileViewCardDetailsItem } from "view360Card/components";
import { Divider } from "@fluentui/react-northstar";

export interface CommissionsTableEhmProps {
    commissionLines: CommissionLine[];
}

export const CommissionsTableEhm: React.FC<CommissionsTableEhmProps> = observer((props) => {
    const headerItems = [
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.line} />,
            key: "line",
            className: styles.lineColumn,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.group} />,
            key: "group",
            className: styles.groupColumn,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.sum} />,
            key: "sum",
            className: styles.alignCenter,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.baseRate} />,
            key: "baseRate",
            className: styles.alignCenter,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.dynamicsRate} />,
            key: "dynamicsRate",
            className: styles.alignCenter,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.locationRate} />,
            key: "locationRate",
            className: styles.alignCenter,
        },
        {
            content: <TextTableHeader content={pl.view360Card.commissionTable.claimRatioRate} />,
            key: "claimRatioRate",
            className: styles.alignCenter,
        },
    ];

    const getRows = () => {
        return props.commissionLines.map((line) => {
            return {
                key: `line-${line.id}`,
                items: [
                    {
                        className: styles.lineColumn,
                        key: `${line.id}-line-name`,
                        content: <RegularText content={line.description} />,
                    },
                    {
                        key: `${line.id}-line-details`,
                        content: getGroupLevelTable(line.groupList ?? []),
                        className: styles.tableCell,
                    },
                ],
            };
        });
    };

    const getRateLevelTable = (commissions: Commission[]) => {
        return <BasicTable rows={commissionLevelTableRows(commissions)} />;
    };

    const commissionLevelTableRows = (commissions: Commission[]) => {
        return commissions.map((commission) => {
            return {
                key: `commision-${commission.id}`,
                items: [
                    {
                        key: `${commission.id}-commission-sum`,
                        content: <RegularText content={formatPercentageNumber(commission.sumRate)} />,
                        className: styles.alignCenter,
                    },
                    {
                        key: `${commission.id}-commission-baseRate`,
                        content: <RegularText content={formatPercentageNumber(commission.baseRate)} />,
                        className: styles.alignCenter,
                    },
                    {
                        key: `${commission.id}-commission-dynamicsRate`,
                        content: <RegularText content={formatPercentageNumber(commission.dynamicsRate)} />,
                        className: styles.alignCenter,
                    },
                    {
                        key: `${commission.id}-commission-locationRate`,
                        content: <RegularText content={formatPercentageNumber(commission.locationRate)} />,
                        className: styles.alignCenter,
                    },
                    {
                        key: `${commission.id}-commission-claimRatioRate`,
                        content: <RegularText content={formatPercentageNumber(commission.claimRatioRate)} />,
                        className: styles.alignCenter,
                    },
                ],
            };
        });
    };

    const getGroupLevelTable = (commissionGroups: CommissionGroup[]) => {
        return <BasicTable rows={groupLevelTableRows(commissionGroups)} />;
    };

    const groupLevelTableRows = (commissionGroups: CommissionGroup[]) => {
        return commissionGroups.map((group) => {
            return {
                key: group.id,
                items: [
                    {
                        key: `${group.id}-group-name`,
                        content: <RegularText content={group.description} />,
                        className: styles.groupColumn,
                    },
                    {
                        key: `${group.id}-line-details`,
                        content: getRateLevelTable(group.commissionList ?? []),
                        className: styles.tableCell,
                    },
                ],
            };
        });
    };

    const getMobileViewDetailItem = (commisionGroups: CommissionGroup[]) => {
        var result: JSX.Element[] = [];

        commisionGroups.forEach((group, idx) => {
            if (idx > 0) {
                result.push(<Divider />);
            }
            group.commissionList?.forEach((commission) => {
                result.push(<RegularText weight="semibold" content={group.description}></RegularText>);
                result.push(
                    <MobileViewCardDetailsItem
                        label={`${pl.view360Card.commissionTable.sum} / ${pl.view360Card.commissionTable.baseRate}`}
                        content={`${formatPercentageNumber(commission.sumRate)} / ${formatPercentageNumber(
                            commission.baseRate
                        )}`}
                    />
                );
                result.push(
                    <MobileViewCardDetailsItem
                        label={`${pl.view360Card.commissionTable.dynamicsRate} / ${pl.view360Card.commissionTable.locationRate} / ${pl.view360Card.commissionTable.claimRatioRate} `}
                        content={`${formatPercentageNumber(commission.dynamicsRate)} / ${formatPercentageNumber(
                            commission.locationRate
                        )} / ${formatPercentageNumber(commission.claimRatioRate)}`}
                    />
                );
            });
        });
        return result;
    };

    const getMobileViewRows = () => {
        if (!props.commissionLines) {
            return null;
        }

        var result: JSX.Element[] = [];

        props.commissionLines.forEach((line) => {
            result.push(
                <MobileViewCard
                    key={line.id}
                    header={line.description || constants.noValue}
                    detailsItems={getMobileViewDetailItem(line.groupList ?? [])}
                />
            );
        });

        return result;
    };

    return <BasicTable rows={getRows()} mobileViewRows={getMobileViewRows()} headerItems={headerItems} />;
});
